// v3.15-1214 FLAG-001 — Frontend feature-flag gate.
//
// Reads company feature flags (source of truth: ADMIN-008 company settings)
// and either renders the child route or shows a graceful denial page.
// Direct-URL access is blocked here; sidebar hiding lives in DashboardLayout.
import { useEffect, useState } from 'react';
import { Loader2, Lock, Settings as SettingsIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const FLAG_LABELS = {
  compensation:  'Compensation',
  payroll:       'Payroll',
  documents:     'Company Documents',
  recognition:   'Recognition & Leaderboards',
  leaderboards:  'Leaderboards',
  badges:        'Badge System',
  career:        'Career Dashboard',
  money_manager: 'Money Manager',
};

// Module-level cache so tab switches don't refetch on every render.
let _flagsCache = null;
let _flagsCompanyId = null;

const _flagsFor = async (companyId) => {
  if (!companyId) return {};
  if (_flagsCache && _flagsCompanyId === companyId) return _flagsCache;
  try {
    const { data } = await api.get(`/company-settings/${companyId}/feature-flags`);
    _flagsCache = data?.flags || {};
    _flagsCompanyId = companyId;
    return _flagsCache;
  } catch { return {}; }
};

export const invalidateFeatureFlagsCache = () => { _flagsCache = null; _flagsCompanyId = null; };

export default function FeatureFlagGate({ flag, children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, enabled: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const flags = await _flagsFor(user?.company_id);
      if (cancelled) return;
      // Backwards-compat: missing/unknown flag → enabled.
      const val = Object.prototype.hasOwnProperty.call(flags, flag) ? !!flags[flag] : true;
      setState({ loading: false, enabled: val });
    })();
    return () => { cancelled = true; };
  }, [user?.company_id, flag]);

  if (state.loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500" data-testid="flag-gate-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking module access…
      </div>
    );
  }

  if (!state.enabled) {
    const label = FLAG_LABELS[flag] || flag;
    return (
      <div className="p-6" data-testid="flag-gate-disabled">
        <div className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="text-xl font-extrabold text-slate-900 mb-2">
            {label} is disabled for your company
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            An administrator has turned this module off in Company Settings. Turn the
            <span className="font-semibold"> {label}</span> feature flag back on to restore access.
          </p>
          <Link
            to="/company-settings"
            data-testid="flag-gate-settings-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold"
          >
            <SettingsIcon className="w-4 h-4" /> Open Company Settings
          </Link>
        </div>
      </div>
    );
  }
  return children;
}
